import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import MyStays from '../components/MyStays';
import DashboardRoleToggle from '../components/DashboardRoleToggle';
import MessagesPanel from '../components/MessagesPanel';
import { useApi, listings as listingsApi, users as usersApi, notifications as notifApi, templates as templatesApi, uploadApi, properties as propertiesApi } from '../services/api';
import PropertySearchWidget from '../components/PropertySearchWidget';
import { useAuth } from '../context/AuthContext';
import { formatPrice, formatDateRange, formatDate } from '../utils/formatters';
import { BuyerDashboardBody } from './BuyerDashboard';

const TABS = [
  { key: 'listings', label: 'My Listings' },
  { key: 'stays', label: 'My Stays' },
  { key: 'templates', label: 'Templates' },
  { key: 'sales', label: 'Sales History' },
  { key: 'notifications', label: 'Notifications' },
  { key: 'messages', label: 'Messages' },
];

const BLANK_FORM = { name: '', unitNumber: '', resortContactEmail: '', unsoldPreference: 'auto_notify_resort', checkInDate: '', checkOutDate: '', ceilingPrice: '', floorPrice: '', maxGuests: 4, notes: '', decayWindowDays: 90, saveAsTemplate: false };

const UNSOLD_OPTIONS = [
  { value: 'auto_notify_resort', label: 'Automatically notify the resort to rent the unit' },
  { value: 'keep_active',        label: 'Keep listing active until check-in' },
  { value: 'email_owner_14d',    label: 'Email me to decide 14 days before check-in' },
];

// Revoke all blob preview URLs to avoid memory leaks
function revokePreviewUrls(files) {
  files.forEach(f => { if (f.previewUrl) URL.revokeObjectURL(f.previewUrl); });
}

export function OwnerDashboardBody({ firstListing = false, onCreated } = {}) {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const stripeStatus = searchParams.get('stripe');
  const bookingConfirmed = searchParams.get('booking') === 'confirmed';
  const startNewListing = searchParams.get('new') === '1' || searchParams.get('tab') === 'new-listing';
  const [tab, setTab] = useState(bookingConfirmed && !firstListing ? 'stays' : 'listings');

  const { data: listingsData, loading: listingsLoading, refetch: refetchListings } = useApi('/listings/owner/mine');
  const { data: txData, loading: txLoading } = useApi('/payments/owner/transactions');
  const { data: notifData, refetch: refetchNotifs } = useApi('/notifications');
  const { data: meData } = useApi('/users/me');
  const { data: templatesData, refetch: refetchTemplates } = useApi('/templates');
  const { data: msgData } = useApi('/messages');

  const listings = listingsData?.listings || [];
  const transactions = txData?.transactions || [];
  const notifications = notifData?.notifications || [];
  const me = meData?.user || user;
  const savedTemplates = templatesData?.templates || [];
  const conversations = useMemo(() => msgData?.conversations || [], [msgData]);
  const unreadMessageCount = conversations.reduce((sum, c) => sum + c.unread_count, 0);
  const unreadByListing = useMemo(() => {
    const map = {};
    conversations.forEach(c => { map[c.listing_id] = c.unread_count; });
    return map;
  }, [conversations]);

  const [messagesThreadListingId, setMessagesThreadListingId] = useState(null);
  function openListingMessages(listingId) {
    setMessagesThreadListingId(listingId);
    setTab('messages');
  }

  const totalRevenue = transactions
    .filter(t => t.status === 'paid_out')
    .reduce((sum, t) => sum + t.owner_payout, 0);

  const [showNewListing, setShowNewListing] = useState(startNewListing || firstListing);
  const [newForm, setNewForm] = useState(BLANK_FORM);
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [photoFiles, setPhotoFiles] = useState([]); // [{ file: File, previewUrl: string }]
  const [uploadingPhotos, setUploadingPhotos] = useState(false);

  const [reportEmailState, setReportEmailState] = useState('idle'); // 'idle' | 'sending' | 'sent' | 'error'

  const [showNewTemplate, setShowNewTemplate] = useState(false);
  const [templateForm, setTemplateForm] = useState({ name: '', unitNumber: '', ceilingPrice: '', floorPrice: '', maxGuests: 4, notes: '', decayWindowDays: 90 });
  const [templateError, setTemplateError] = useState('');
  const [templateLoading, setTemplateLoading] = useState(false);
  const [savingTemplateFromListingId, setSavingTemplateFromListingId] = useState(null);
  const [activatingId, setActivatingId] = useState(null);

  // Pre-fill resort email from property cache when a property is selected
  useEffect(() => {
    if (selectedProperty?.resort_contact_email && !newForm.resortContactEmail) {
      setNewForm(f => ({ ...f, resortContactEmail: selectedProperty.resort_contact_email }));
    }
    setReportEmailState('idle');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProperty]);

  // Real-time pricing warning: show when check-in is < 7 days away (floor price imminent)
  const pricingWarning = useMemo(() => {
    const ceiling = parseFloat(newForm.ceilingPrice);
    const floor   = parseFloat(newForm.floorPrice);
    const checkIn = newForm.checkInDate;
    if (!ceiling || !floor || !checkIn || ceiling <= floor) return null;
    const today        = new Date(); today.setHours(0, 0, 0, 0);
    const daysUntilFloor = Math.floor((new Date(checkIn) - today) / (1000 * 60 * 60 * 24));
    if (daysUntilFloor <= 0 || daysUntilFloor >= 7) return null;
    return `At this pace, your price will reach your floor of $${floor.toFixed(2)} in ${daysUntilFloor} day${daysUntilFloor !== 1 ? 's' : ''} — consider adjusting your ceiling, floor, or decay window for better results.`;
  }, [newForm.ceilingPrice, newForm.floorPrice, newForm.checkInDate]);

  async function getOnboardingLink() {
    try {
      const res = await usersApi.getOnboarding();
      window.location.href = res.data.url;
    } catch (e) {
      alert('Could not get Stripe onboarding link. Please try again.');
    }
  }

  function handlePhotoSelect(e) {
    const selected = Array.from(e.target.files).map(file => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setPhotoFiles(prev => [...prev, ...selected].slice(0, 10));
    e.target.value = '';
  }

  function removePhoto(index) {
    setPhotoFiles(prev => {
      URL.revokeObjectURL(prev[index].previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  }

  function applyTemplate(tpl) {
    setNewForm(f => ({
      ...f,
      name: tpl.name,
      unitNumber: tpl.unit_number || '',
      ceilingPrice: (tpl.ceiling_price / 100).toFixed(2),
      floorPrice: (tpl.floor_price / 100).toFixed(2),
      maxGuests: tpl.max_guests,
      notes: tpl.notes || '',
      decayWindowDays: tpl.decay_window_days,
      checkInDate: '',
      checkOutDate: '',
    }));
    setShowNewListing(true);
    setTab('listings');
  }

  async function handleReportEmail() {
    if (!selectedProperty) return;
    setReportEmailState('sending');
    try {
      await propertiesApi.reportResortEmail(selectedProperty.id);
      setReportEmailState('sent');
    } catch {
      setReportEmailState('error');
    }
  }

  async function createListing(e) {
    e.preventDefault();
    setFormError('');
    if (!selectedProperty) {
      setFormError('Please select a property before creating a listing.');
      return;
    }
    setFormLoading(true);

    try {
      // Upload photos first if any were selected
      let imageUrls = [];
      if (photoFiles.length > 0) {
        setUploadingPhotos(true);
        const res = await uploadApi.images(photoFiles.map(p => p.file));
        imageUrls = res.data.urls;
        setUploadingPhotos(false);
      }

      await listingsApi.create({
        propertyId: selectedProperty.id,
        unitNumber: newForm.unitNumber,
        resortContactEmail: newForm.resortContactEmail || undefined,
        unsoldPreference: newForm.unsoldPreference,
        checkInDate: newForm.checkInDate,
        checkOutDate: newForm.checkOutDate,
        ceilingPrice: Math.round(parseFloat(newForm.ceilingPrice) * 100),
        floorPrice: Math.round(parseFloat(newForm.floorPrice) * 100),
        maxGuests: parseInt(newForm.maxGuests),
        notes: newForm.notes,
        decayWindowDays: parseInt(newForm.decayWindowDays),
        imageUrls,
      });

      if (newForm.saveAsTemplate && newForm.name) {
        await templatesApi.create({
          name: newForm.name,
          unitNumber: newForm.unitNumber,
          ceilingPrice: Math.round(parseFloat(newForm.ceilingPrice) * 100),
          floorPrice: Math.round(parseFloat(newForm.floorPrice) * 100),
          maxGuests: parseInt(newForm.maxGuests),
          notes: newForm.notes,
          decayWindowDays: parseInt(newForm.decayWindowDays),
        });
        refetchTemplates();
      }

      revokePreviewUrls(photoFiles);
      setPhotoFiles([]);
      setSelectedProperty(null);
      setShowNewListing(false);
      setNewForm(BLANK_FORM);
      refetchListings();
      if (onCreated) onCreated();
    } catch (err) {
      setUploadingPhotos(false);
      setFormError(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Could not create listing.');
    } finally {
      setFormLoading(false);
    }
  }

  async function activateListing(listingId) {
    setActivatingId(listingId);
    try {
      await listingsApi.activate(listingId);
      refetchListings();
    } catch (err) {
      alert(err.response?.data?.error || 'Could not activate listing.');
    } finally {
      setActivatingId(null);
    }
  }

  async function saveListingAsTemplate(listing) {
    setSavingTemplateFromListingId(listing.id);
    try {
      const name = window.prompt('Template name:', listing.unit_number ? `${listing.unit_number}` : 'My Template');
      if (!name) return;
      await templatesApi.create({
        name,
        propertyId: listing.property_id,
        unitNumber: listing.unit_number,
        ceilingPrice: listing.ceiling_price,
        floorPrice: listing.floor_price,
        maxGuests: listing.max_guests,
        notes: listing.notes,
        decayWindowDays: listing.decay_window_days,
      });
      refetchTemplates();
    } catch (err) {
      alert(err.response?.data?.error || 'Could not save template.');
    } finally {
      setSavingTemplateFromListingId(null);
    }
  }

  async function createTemplate(e) {
    e.preventDefault();
    setTemplateError('');
    setTemplateLoading(true);
    try {
      await templatesApi.create({
        name: templateForm.name,
        unitNumber: templateForm.unitNumber,
        ceilingPrice: Math.round(parseFloat(templateForm.ceilingPrice) * 100),
        floorPrice: Math.round(parseFloat(templateForm.floorPrice) * 100),
        maxGuests: parseInt(templateForm.maxGuests),
        notes: templateForm.notes,
        decayWindowDays: parseInt(templateForm.decayWindowDays),
      });
      setShowNewTemplate(false);
      setTemplateForm({ name: '', unitNumber: '', ceilingPrice: '', floorPrice: '', maxGuests: 4, notes: '', decayWindowDays: 90 });
      refetchTemplates();
    } catch (err) {
      setTemplateError(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Could not save template.');
    } finally {
      setTemplateLoading(false);
    }
  }

  async function deleteTemplate(id) {
    if (!window.confirm('Delete this template?')) return;
    try {
      await templatesApi.remove(id);
      refetchTemplates();
    } catch (err) {
      alert('Could not delete template.');
    }
  }

  async function markAllRead() {
    await notifApi.markAllRead();
    refetchNotifs();
  }

  const draftListings = listings.filter(l => l.status === 'draft');
  const activeListings = listings.filter(l => l.status !== 'draft');

  return (
      <div className="dashboard">
        {firstListing ? (
          <>
            <h1>List Your Property</h1>
            <p className="dashboard-subtitle">
              Create your first listing to start hosting on ShareTime.
            </p>
          </>
        ) : (
          <>
            <h1>Owner Dashboard</h1>
            <p className="dashboard-subtitle">
              Welcome back, {me?.first_name}
              {me?.listing_fee_credits > 0 && (
                <span style={{ marginLeft: '0.75rem', background: 'var(--success-bg)', color: 'var(--success)', borderRadius: '999px', padding: '0.2rem 0.6rem', fontSize: 'var(--text-sm)', fontWeight: 600 }}>
                  {formatPrice(me.listing_fee_credits)} listing credit
                </span>
              )}
            </p>
          </>
        )}

        {!firstListing && !me?.stripe_onboarding_complete && (
          <div className="stripe-banner">
            <p><strong>Connect your bank account</strong> to receive payouts when your listings sell.</p>
            <button className="btn btn-primary btn-sm" onClick={getOnboardingLink}>
              Set up Stripe Connect →
            </button>
          </div>
        )}

        {!firstListing && stripeStatus === 'success' && (
          <div className="alert alert-success">Stripe Connect setup complete! You're ready to get paid.</div>
        )}

        {!firstListing && bookingConfirmed && (
          <div className="alert alert-success">
            <div>Booking confirmed! Check your email for details.</div>
            <div style={{ marginTop: '0.5rem', fontSize: 'var(--text-base)', opacity: 0.85 }}>
              Note: The resort may require a credit card at check-in for incidentals. Please have a card available when you arrive.
            </div>
          </div>
        )}

        {!firstListing && (
          <div className="stats-row">
            <div className="stat-card">
              <div className="stat-value">{listings.filter(l => l.status === 'active').length}</div>
              <div className="stat-label">Active Listings</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{draftListings.length}</div>
              <div className="stat-label">Drafts</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{listings.filter(l => l.status === 'sold').length}</div>
              <div className="stat-label">Stays Sold</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{formatPrice(totalRevenue)}</div>
              <div className="stat-label">Total Paid Out</div>
            </div>
          </div>
        )}

        {!firstListing && (
          <div className="dashboard-tabs">
            {TABS.map(t => (
              <button key={t.key} className={`tab-btn ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
                {t.label}
                {t.key === 'notifications' && notifications.filter(n => !n.read).length > 0 && (
                  <span style={{ marginLeft: '0.4rem', background: 'var(--danger)', color: 'var(--surface)', borderRadius: '999px', fontSize: 'var(--text-xs)', padding: '0.1rem 0.45rem', fontWeight: 700 }}>
                    {notifications.filter(n => !n.read).length}
                  </span>
                )}
                {t.key === 'templates' && savedTemplates.length > 0 && (
                  <span style={{ marginLeft: '0.4rem', background: 'var(--ink-muted)', color: 'var(--surface)', borderRadius: '999px', fontSize: 'var(--text-xs)', padding: '0.1rem 0.45rem', fontWeight: 700 }}>
                    {savedTemplates.length}
                  </span>
                )}
                {t.key === 'messages' && unreadMessageCount > 0 && (
                  <span style={{ marginLeft: '0.4rem', background: 'var(--danger)', color: 'var(--surface)', borderRadius: '999px', fontSize: 'var(--text-xs)', padding: '0.1rem 0.45rem', fontWeight: 700 }}>
                    {unreadMessageCount}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* ── Messages tab ── */}
        {!firstListing && tab === 'messages' && <MessagesPanel initialListingId={messagesThreadListingId} />}

        {/* ── Listings tab ── */}
        {(firstListing || tab === 'listings') && (
          <>
            {!firstListing && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                <button className="btn btn-primary btn-sm" onClick={() => { setShowNewListing(v => !v); setNewForm(BLANK_FORM); setFormError(''); revokePreviewUrls(photoFiles); setPhotoFiles([]); setSelectedProperty(null); }}>
                  {showNewListing ? 'Cancel' : '+ New Listing'}
                </button>
              </div>
            )}

            {(showNewListing || firstListing) && (
              <div className="card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ fontWeight: 600 }}>Create New Listing</h3>
                  {savedTemplates.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-muted)' }}>Start from template:</span>
                      <select
                        style={{ fontSize: 'var(--text-sm)', padding: '0.3rem 0.5rem', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}
                        defaultValue=""
                        onChange={e => { const tpl = savedTemplates.find(t => t.id === e.target.value); if (tpl) applyTemplate(tpl); }}
                      >
                        <option value="" disabled>Choose…</option>
                        {savedTemplates.map(t => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {formError && <div className="alert alert-error">{formError}</div>}
                <form onSubmit={createListing}>
                  <div className="form-group" style={{ marginBottom: '1.25rem' }}>
                    <label>Property / Resort <span style={{ color: 'var(--danger)' }}>*</span></label>
                    <PropertySearchWidget value={selectedProperty} onChange={setSelectedProperty} />
                    <span className="field-hint">Type your resort name or address to search</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem' }}>
                    <div className="form-group">
                      <label>Unit Number</label>
                      <input placeholder="e.g. Unit 4B" value={newForm.unitNumber} onChange={e => setNewForm(f => ({ ...f, unitNumber: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label>Resort Front Desk Email <span style={{ fontWeight: 400, color: 'var(--ink-muted)' }}>(optional)</span></label>
                      <input type="email" placeholder="frontdesk@resort.com" value={newForm.resortContactEmail} onChange={e => setNewForm(f => ({ ...f, resortContactEmail: e.target.value }))} />
                      {(() => {
                        const cached = selectedProperty?.resort_contact_email;
                        const current = newForm.resortContactEmail;
                        const isCached = current && cached && current === cached;
                        const isNew    = current && current !== cached;
                        return (
                          <>
                            {isNew && (
                              <div style={{ marginTop: '0.4rem', padding: '0.5rem 0.75rem', background: 'var(--warning-bg)', border: '1px solid var(--warning-bg)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-sm)', color: 'var(--warning)' }}>
                                This email will be saved for future owners at this property — please verify it is correct before submitting.
                              </div>
                            )}
                            {isCached && (
                              <div style={{ marginTop: '0.4rem', fontSize: 'var(--text-sm)', color: 'var(--ink-muted)' }}>
                                Pre-filled from a previous listing at this resort.{' '}
                                {reportEmailState === 'sent' ? (
                                  <span style={{ color: 'var(--success)', fontWeight: 600 }}>Report sent — thank you.</span>
                                ) : reportEmailState === 'error' ? (
                                  <span style={{ color: 'var(--danger)' }}>Could not send report. Please try again.</span>
                                ) : (
                                  <button
                                    type="button"
                                    disabled={reportEmailState === 'sending'}
                                    onClick={handleReportEmail}
                                    style={{ background: 'none', border: 'none', padding: 0, color: '#2563EB', cursor: 'pointer', textDecoration: 'underline', fontSize: 'var(--text-sm)' }}
                                  >
                                    {reportEmailState === 'sending' ? 'Sending…' : 'Report incorrect email'}
                                  </button>
                                )}
                              </div>
                            )}
                            {!isNew && !isCached && (
                              <span className="field-hint">Used for guest authorization letters — saved to this resort for future listings</span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    <div className="form-group">
                      <label>Check-in Date</label>
                      <input type="date" required value={newForm.checkInDate} onChange={e => setNewForm(f => ({ ...f, checkInDate: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label>Check-out Date</label>
                      <input type="date" required value={newForm.checkOutDate} onChange={e => setNewForm(f => ({ ...f, checkOutDate: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label>Ceiling Price ($)</label>
                      <input type="number" min="1" step="0.01" required placeholder="e.g. 1800" value={newForm.ceilingPrice} onChange={e => setNewForm(f => ({ ...f, ceilingPrice: e.target.value }))} />
                      <span className="field-hint">Your ideal asking price</span>
                    </div>
                    <div className="form-group">
                      <label>Floor Price ($)</label>
                      <input type="number" min="1" step="0.01" required placeholder="e.g. 800" value={newForm.floorPrice} onChange={e => setNewForm(f => ({ ...f, floorPrice: e.target.value }))} />
                      <span className="field-hint">Minimum you'll accept</span>
                    </div>
                    <div className="form-group">
                      <label>Max Guests</label>
                      <input type="number" min="1" max="20" value={newForm.maxGuests} onChange={e => setNewForm(f => ({ ...f, maxGuests: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label>Decay Window (days)</label>
                      <input type="number" min="7" max="365" value={newForm.decayWindowDays} onChange={e => setNewForm(f => ({ ...f, decayWindowDays: e.target.value }))} />
                      <span className="field-hint">How many days before check-in the price starts dropping</span>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Notes for Buyer (optional)</label>
                    <textarea rows={3} placeholder="Any details about the unit, view, access…" value={newForm.notes} onChange={e => setNewForm(f => ({ ...f, notes: e.target.value }))} />
                  </div>

                  <div className="form-group">
                    <label>If your listing doesn't sell, what should we do?</label>
                    <select value={newForm.unsoldPreference} onChange={e => setNewForm(f => ({ ...f, unsoldPreference: e.target.value }))}>
                      {UNSOLD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <span className="field-hint">
                      {newForm.unsoldPreference === 'auto_notify_resort' && '7 days before check-in, ShareTime will send a unit-release notice to the resort on your behalf'}
                      {newForm.unsoldPreference === 'keep_active' && 'Your listing stays visible to buyers right up until check-in'}
                      {newForm.unsoldPreference === 'email_owner_14d' && 'You\'ll get an email 14 days before check-in asking how you\'d like to proceed'}
                    </span>
                  </div>

                  {/* Photo upload */}
                  <div className="form-group">
                    <label>Photos (optional, up to 10)</label>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      multiple
                      onChange={handlePhotoSelect}
                      style={{ fontSize: 'var(--text-base)' }}
                    />
                    {photoFiles.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.75rem' }}>
                        {photoFiles.map((p, i) => (
                          <div key={i} style={{ position: 'relative', display: 'inline-block' }}>
                            <img
                              src={p.previewUrl}
                              alt={`preview ${i + 1}`}
                              style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', display: 'block' }}
                            />
                            <button
                              type="button"
                              onClick={() => removePhoto(i)}
                              style={{
                                position: 'absolute', top: -6, right: -6,
                                width: 20, height: 20, borderRadius: '50%',
                                background: 'var(--danger)', color: 'var(--surface)', border: 'none',
                                cursor: 'pointer', fontSize: 'var(--text-xs)', fontWeight: 700,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                lineHeight: 1,
                              }}
                              aria-label="Remove photo"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: '1rem', marginTop: '0.5rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginBottom: newForm.saveAsTemplate ? '0.75rem' : 0 }}>
                      <input
                        type="checkbox"
                        style={{ width: 'auto' }}
                        checked={newForm.saveAsTemplate}
                        onChange={e => setNewForm(f => ({ ...f, saveAsTemplate: e.target.checked }))}
                      />
                      <span style={{ fontWeight: 500 }}>Save as template for future listings</span>
                    </label>
                    {newForm.saveAsTemplate && (
                      <div className="form-group" style={{ maxWidth: 320, marginBottom: 0 }}>
                        <label>Template name</label>
                        <input
                          required={newForm.saveAsTemplate}
                          placeholder="e.g. Ocean Suite Unit 4B"
                          value={newForm.name}
                          onChange={e => setNewForm(f => ({ ...f, name: e.target.value }))}
                        />
                      </div>
                    )}
                  </div>

                  {pricingWarning && (
                    <div style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning)', borderRadius: 'var(--radius-md)', padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: 'var(--text-base)', color: 'var(--warning)' }}>
                      ⚠ {pricingWarning}
                    </div>
                  )}

                  <div style={{ marginTop: '1rem' }}>
                    <button type="submit" className="btn btn-primary" disabled={formLoading}>
                      {uploadingPhotos ? `Uploading ${photoFiles.length} photo${photoFiles.length !== 1 ? 's' : ''}…` : formLoading ? 'Creating…' : 'Create Draft Listing'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {!firstListing && (listingsLoading ? <p style={{ color: 'var(--ink-muted)' }}>Loading listings…</p> : (
              listings.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--ink-muted)' }}>
                  <p>No listings yet. Create your first one above.</p>
                </div>
              ) : (
                <>
                  {draftListings.length > 0 && (
                    <div style={{ marginBottom: '1.5rem' }}>
                      <h3 style={{ fontWeight: 600, fontSize: 'var(--text-base)', color: 'var(--ink-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.6rem' }}>Drafts</h3>
                      <div className="table-wrap">
                        <table>
                          <thead>
                            <tr>
                              <th>Dates</th>
                              <th>Unit</th>
                              <th>Ceiling / Floor</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {draftListings.map(l => (
                              <tr key={l.id}>
                                <td>{formatDateRange(l.check_in_date, l.check_out_date)}</td>
                                <td>{l.unit_number || '—'}</td>
                                <td style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-muted)' }}>{formatPrice(l.ceiling_price)} → {formatPrice(l.floor_price)}</td>
                                <td>
                                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                                    <button
                                      className="btn btn-primary btn-sm"
                                      disabled={activatingId === l.id}
                                      onClick={() => activateListing(l.id)}
                                    >
                                      {activatingId === l.id ? 'Activating…' : 'Activate'}
                                    </button>
                                    <button
                                      className="btn btn-outline btn-sm"
                                      disabled={savingTemplateFromListingId === l.id}
                                      onClick={() => saveListingAsTemplate(l)}
                                    >
                                      Save as Template
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {activeListings.length > 0 && (
                    <div>
                      {draftListings.length > 0 && (
                        <h3 style={{ fontWeight: 600, fontSize: 'var(--text-base)', color: 'var(--ink-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.6rem' }}>Active & Past</h3>
                      )}
                      <div className="table-wrap">
                        <table>
                          <thead>
                            <tr>
                              <th>Dates</th>
                              <th>Unit</th>
                              <th>Current Price</th>
                              <th>Ceiling / Floor</th>
                              <th>Status</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {activeListings.map(l => (
                              <tr key={l.id}>
                                <td>{formatDateRange(l.check_in_date, l.check_out_date)}</td>
                                <td>{l.unit_number || '—'}</td>
                                <td><strong>{formatPrice(l.current_price)}</strong></td>
                                <td style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-muted)' }}>{formatPrice(l.ceiling_price)} → {formatPrice(l.floor_price)}</td>
                                <td><span className={`badge badge-${l.status}`}>{l.status}</span></td>
                                <td>
                                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                                    <button
                                      className="btn btn-outline btn-sm"
                                      onClick={() => openListingMessages(l.id)}
                                      style={{ whiteSpace: 'nowrap' }}
                                    >
                                      Messages
                                      {unreadByListing[l.id] > 0 && (
                                        <span style={{ marginLeft: '0.3rem', background: 'var(--danger)', color: 'var(--surface)', borderRadius: '999px', fontSize: 'var(--text-xs)', padding: '0.05rem 0.4rem', fontWeight: 700 }}>
                                          {unreadByListing[l.id]}
                                        </span>
                                      )}
                                    </button>
                                    <button
                                      className="btn btn-outline btn-sm"
                                      disabled={savingTemplateFromListingId === l.id}
                                      onClick={() => saveListingAsTemplate(l)}
                                      style={{ whiteSpace: 'nowrap' }}
                                    >
                                      Save as Template
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )
            ))}
          </>
        )}

        {/* ── Stays tab (bookings made as a buyer) ── */}
        {!firstListing && tab === 'stays' && <MyStays />}

        {/* ── Templates tab ── */}
        {!firstListing && tab === 'templates' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <p style={{ color: 'var(--ink-muted)', fontSize: 'var(--text-base)', margin: 0 }}>
                Templates store your unit details and pricing so you can create new listings without re-entering everything.
              </p>
              <button className="btn btn-primary btn-sm" onClick={() => { setShowNewTemplate(v => !v); setTemplateError(''); }}>
                {showNewTemplate ? 'Cancel' : '+ New Template'}
              </button>
            </div>

            {showNewTemplate && (
              <div className="card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
                <h3 style={{ fontWeight: 600, marginBottom: '1rem' }}>Create Template</h3>
                {templateError && <div className="alert alert-error">{templateError}</div>}
                <form onSubmit={createTemplate}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem' }}>
                    <div className="form-group">
                      <label>Template Name</label>
                      <input required placeholder="e.g. Ocean Suite Unit 4B" value={templateForm.name} onChange={e => setTemplateForm(f => ({ ...f, name: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label>Unit Number</label>
                      <input placeholder="e.g. Unit 4B" value={templateForm.unitNumber} onChange={e => setTemplateForm(f => ({ ...f, unitNumber: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label>Ceiling Price ($)</label>
                      <input type="number" min="1" step="0.01" required placeholder="e.g. 1800" value={templateForm.ceilingPrice} onChange={e => setTemplateForm(f => ({ ...f, ceilingPrice: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label>Floor Price ($)</label>
                      <input type="number" min="1" step="0.01" required placeholder="e.g. 800" value={templateForm.floorPrice} onChange={e => setTemplateForm(f => ({ ...f, floorPrice: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label>Max Guests</label>
                      <input type="number" min="1" max="20" value={templateForm.maxGuests} onChange={e => setTemplateForm(f => ({ ...f, maxGuests: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label>Decay Window (days)</label>
                      <input type="number" min="7" max="365" value={templateForm.decayWindowDays} onChange={e => setTemplateForm(f => ({ ...f, decayWindowDays: e.target.value }))} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Notes for Buyer (optional)</label>
                    <textarea rows={2} placeholder="Any details about the unit, view, access…" value={templateForm.notes} onChange={e => setTemplateForm(f => ({ ...f, notes: e.target.value }))} />
                  </div>
                  <button type="submit" className="btn btn-primary" disabled={templateLoading}>
                    {templateLoading ? 'Saving…' : 'Save Template'}
                  </button>
                </form>
              </div>
            )}

            {savedTemplates.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2.5rem 0', color: 'var(--ink-muted)' }}>
                <p>No templates yet.</p>
                <p style={{ fontSize: 'var(--text-base)', marginTop: '0.5rem' }}>
                  Create a template or check "Save as template" when creating a listing.
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {savedTemplates.map(t => (
                  <div key={t.id} className="card" style={{ padding: '1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: '0.2rem' }}>{t.name}</div>
                      <div style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-muted)' }}>
                        {t.unit_number && <span>{t.unit_number} · </span>}
                        {formatPrice(t.ceiling_price)} → {formatPrice(t.floor_price)} · {t.max_guests} guests · {t.decay_window_days}d decay
                      </div>
                      {t.notes && (
                        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-faint)', marginTop: '0.2rem' }}>{t.notes}</div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => applyTemplate(t)}
                      >
                        Use Template
                      </button>
                      <button
                        className="btn btn-outline btn-sm"
                        style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }}
                        onClick={() => deleteTemplate(t.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── Sales tab ── */}
        {!firstListing && tab === 'sales' && (
          txLoading ? <p style={{ color: 'var(--ink-muted)' }}>Loading sales…</p> : (
            transactions.length === 0 ? (
              <p style={{ color: 'var(--ink-muted)' }}>No sales yet.</p>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Dates</th>
                      <th>Sale Price</th>
                      <th>Your Payout</th>
                      <th>Status</th>
                      <th>Sold On</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map(t => (
                      <tr key={t.id}>
                        <td>{formatDateRange(t.check_in_date, t.check_out_date)}</td>
                        <td>{formatPrice(t.amount)}</td>
                        <td>{formatPrice(t.owner_payout)}</td>
                        <td><span className={`badge badge-${t.status}`}>{t.status.replace('_', ' ')}</span></td>
                        <td style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-muted)' }}>{formatDate(t.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )
        )}

        {/* ── Notifications tab ── */}
        {!firstListing && tab === 'notifications' && (
          <>
            {notifications.some(n => !n.read) && (
              <button className="btn btn-outline btn-sm" style={{ marginBottom: '1rem' }} onClick={markAllRead}>
                Mark all as read
              </button>
            )}
            {notifications.length === 0 ? (
              <p style={{ color: 'var(--ink-muted)' }}>No notifications yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {notifications.map(n => (
                  <div key={n.id} className="card" style={{ padding: '1rem', borderLeft: n.read ? '3px solid var(--border)' : '3px solid var(--brand)', opacity: n.read ? 0.7 : 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                      <strong style={{ fontSize: 'var(--text-base)' }}>{n.title}</strong>
                      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--ink-faint)' }}>{formatDate(n.created_at)}</span>
                    </div>
                    <p style={{ fontSize: 'var(--text-base)', color: 'var(--ink)' }}>{n.message}</p>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
  );
}

export default function OwnerDashboard() {
  const { data: listingsData, refetch: refetchListings } = useApi('/listings/owner/mine');
  const hasListings = (listingsData?.listings || []).length > 0;
  const [view, setView] = useState('hosting');

  return (
    <div className="page">
      <Navbar />
      {hasListings && <DashboardRoleToggle view={view} onChange={setView} />}
      {hasListings && view === 'staying'
        ? <BuyerDashboardBody hasListings={hasListings} />
        : <OwnerDashboardBody firstListing={!hasListings} onCreated={refetchListings} />}
      <Footer />
    </div>
  );
}
